import { createSelector } from '@reduxjs/toolkit';

import { FileManagerShape, Column, FileManagerRow } from '../model';
import { Page } from '../../../models/page';
import { CHNode, CHNodeIsFolder, NodeLabel } from '../../../../general/node';
import { RootSelector, getStore } from '../../../models/rootSelector';
import { getNodeId, getPage } from '../../app/selectors';
import { getInProgress as getInProgressSearch } from '../../search/selectors/selectors';
import { SortByInterface } from '../../../../general/utils/useSort';
import { FilterReducerShape } from '../../../../general/modules/filter/reducers';
import { BLTeam } from '../../../../general/model';
import { keyBy } from 'lodash';

export const getFileManager: RootSelector<FileManagerShape> = createSelector(
  getStore,
  (x) => x.fileManager,
);
export const getColumnToggles: RootSelector<Partial<Record<Column, boolean>>> = createSelector(
  getFileManager,
  (x) => x.columnToggles,
);

export const getInProgress: RootSelector<boolean> = createSelector(
  getFileManager,
  (fm) => fm.inProgress,
);

export const getSortBy: RootSelector<SortByInterface> = createSelector(
  getFileManager,
  (fm) => fm.sortBy,
);

/**
 * Идёт ли догрузка бизнес-данных креативов (`GET /bl/creatives`).
 *
 * Отдельный селектор, а не чтение поля из `getFileManager` на месте: флаг
 * читает каждая ячейка бизнес-колонок (`WithLoader`), а их на экране под
 * сотню. Подписка на весь срез `fileManager` перерендеривала бы их все на
 * любое его изменение — выделение строки, приход узлов, смена сортировки.
 */
export const getBlLoading: RootSelector<boolean> = createSelector(
  getFileManager,
  (fm) => fm.blLoading,
);

export const getTableInProgress: RootSelector<boolean> = createSelector(
  [getPage, getInProgress, getInProgressSearch],
  (page, inProgress, inProgressSearch) => {
    return page === Page.SEARCH ? inProgressSearch : inProgress;
  },
);

export const getGenres = createSelector(getFileManager, (x) => x.genres);
export const getIntros = createSelector(getFileManager, (x) => x.intros);

export const getNodes = createSelector(
  [getFileManager, getIntros, getGenres],
  (fm, intros, genres) => {
    const introsByID = keyBy(intros, (x) => x.id);
    const genresByID = keyBy(genres, (x) => x.id);
    return fm.nodes.map((node): FileManagerRow => {
      if (node.creative === null) return node;

      const introID = node.creative.intro?.id;
      const genreID = node.creative.genre?.id;
      const intro = introID !== undefined ? introsByID[introID] ?? null : null;
      const genre = genreID !== undefined ? genresByID[genreID] ?? null : null;

      // Пересобираем массив жанров через актуальный словарь (чтобы вместо
      // пустышек "<Genre 123>", созданных в makeCreativeFolder, показывать
      // реальные имена).
      const resolvedGenres = node.creative.genres.map((g) => genresByID[g.id] ?? g);
      return {
        ...node,
        creative: {
          ...node.creative,
          intro: intro ?? node.creative.intro,
          genre: genre ?? node.creative.genre,
          genres: resolvedGenres,
        },
      };
    });
  },
);
export const getNodeIDs: RootSelector<number[]> = createSelector(getNodes, (nodes) =>
  nodes.map((x) => x.id),
);
export const getActiveFilters: RootSelector<FilterReducerShape<Column>> = createSelector(
  getStore,
  (x) => x.fileManagerFilter,
);

export const getSelectedNodesByFolderID: RootSelector<Record<number, CHNode[]>> = createSelector(
  getFileManager,
  (fm) => fm.selectedNodesByFolderID,
);
export const getselectedNodesByFolderIDFolders: RootSelector<Record<number, NodeLabel>> =
  createSelector(getFileManager, (fm) => fm.foldersByID);
export const getSelectedNodesInAllFolders: RootSelector<CHNode[]> = createSelector(
  getSelectedNodesByFolderID,
  (nodesByFolderID) => Object.values(nodesByFolderID).flat(),
);
export const getSingleSelectedNode: RootSelector<CHNode | null> = createSelector(
  getSelectedNodesInAllFolders,
  (nodes) => {
    if (nodes.length !== 1) return null;
    return nodes[0];
  },
);
export const getSelectedNodesInCurrentFolder: RootSelector<CHNode[]> = createSelector(
  [getFileManager, getNodeId],
  (fm, nodeID) => (nodeID !== null ? fm.selectedNodesByFolderID[nodeID] ?? [] : []),
);
export const getSelectedNodesOutsideCurrentFolderCount = createSelector(
  [getSelectedNodesInAllFolders, getSelectedNodesInCurrentFolder],
  (nodesInAllFolders, nodesInCurrentFolder) => {
    return nodesInAllFolders.length - nodesInCurrentFolder.length;
  },
);
export const getSelectedNodeIDsInCurrentFolder: RootSelector<number[]> = createSelector(
  [getSelectedNodesInCurrentFolder],
  (nodes) => nodes.map((x) => x.id),
);
// по сути это то же самое, что и getSelectedNodesInCurrentFolder, но немного
// дороже, зато дает на выходе FileManagerRow[] вместо CHNode[]
export const getSelectedRowsInCurrentFolder: RootSelector<FileManagerRow[]> = createSelector(
  [getFileManager, getSelectedNodeIDsInCurrentFolder],
  (fm, nodeIDs) => fm.nodes.filter((node) => nodeIDs.includes(node.id)),
);
export const getSelectedNodeIDsInAllFolders: RootSelector<number[]> = createSelector(
  [getSelectedNodesInAllFolders],
  (nodes) => nodes.map((x) => x.id),
);
export const getSelectedFolders: RootSelector<CHNode[]> = createSelector(
  [getSelectedNodesInAllFolders],
  (nodes) => nodes.filter(CHNodeIsFolder),
);
export const getTeams: RootSelector<BLTeam[] | null> = createSelector(
  getFileManager,
  (fm) => fm.blTeams,
);

export const getNodeById: RootSelector<Record<string, CHNode>> = createSelector(
  [getNodes],
  (nodes) => {
    const result: Record<string, CHNode> = {};
    nodes.forEach((node) => {
      result[node.id] = node;
    });
    return result;
  },
);
